/**
 * Application state: the session, the vault, and the theme.
 *
 * The important idea here is that **being signed in and being able to read your
 * files are different states**, and the app has to represent both. The session
 * cookie proves who you are; it cannot open the vault, because the server holds
 * no key that could. So a signed-in user with a locked vault sees the chrome
 * and no file names — which is also an honest picture of what the server knows.
 *
 * Four states, and every screen switches on them:
 *
 *   signedOut  → send them to mauvely.com to sign in
 *   noVault    → first run: generate keys and a recovery phrase
 *   locked     → ask for the password (or the phrase) and unlock in-browser
 *   unlocked   → the app proper
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from 'react';
import { api, ApiError, type CloudConfig, type MeResponse, type StorageState } from './api';
import {
  applyBranding,
  applyCachedBranding,
  getBranding,
  setProduct,
  subscribeBranding
} from '@shared/apply-branding';
import type { Branding } from '@shared/branding';
import { startPresence, stopPresence } from '@shared/presence-client';
import { completePendingShares, forgetKeys } from './nodes';
import { completePendingWorkspaceKeys, forgetWorkspaceKeys } from './workspaces';
import { completePendingProjectKeys } from './projects';
import { isPickerPath, pickerGateHere } from './picker';
import {
  getVaultState,
  isRestorable,
  lock,
  restoreTrusted,
  subscribe,
  type VaultState
} from './crypto/vault';

export type AppStatus =
  | 'loading'
  | 'signedOut'
  | 'secondFactor'
  | 'unreachable'
  | 'noVault'
  | 'locked'
  | 'unlocked';

interface AppState {
  status: AppStatus;
  config: CloudConfig;
  me: MeResponse | null;
  storage: StorageState | null;
  vault: VaultState;
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  refresh: () => Promise<void>;
  refreshStorage: () => Promise<void>;
  lockVault: () => void;
  /** Why the session could not be loaded, when `status` is 'unreachable'. */
  loadError: string | null;
  /** Retry the mount-time load. Bound to the button on the unreachable screen. */
  retry: () => void;
  /** Invitations completed this session, once their recipient had a vault.
   *  Worth telling the owner: from their side the share looked done already. */
  completedShares: number;
}

const AppContext = createContext<AppState | null>(null);

/** Used until /api/cloud/config answers. Everything off is the safe default. */
const FALLBACK_CONFIG: CloudConfig = {
  storage: false,
  email: false,
  bytesPerMb: 1024 * 1024,
  maxFileBytes: 5 * 1024 * 1024 * 1024,
  enterpriseUrl: ''
};

/**
 * The organisation's branding from the last load, applied before anything
 * renders.
 *
 * At module scope on purpose. A branded site otherwise paints Mauvely violet
 * under the word `cloud.`, asks who the viewer is, and repaints — a flash of
 * the wrong company on every navigation, for a customer who has paid not to
 * see one. The server's answer replaces this the moment it arrives, so a stale
 * value survives exactly one paint.
 */
setProduct({ name: 'Mauvely Cloud', short: 'Cloud' });
applyCachedBranding();

const THEME_KEY = 'mauvely-cloud-theme';

/**
 * Theme is the one thing worth persisting locally.
 *
 * It is a display preference and reveals nothing, unlike anything key-shaped —
 * which is why `crypto/vault.ts` refuses localStorage entirely and this file
 * uses it without hesitation.
 */
function initialTheme(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // Private mode, or storage blocked. The default is fine.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<CloudConfig>(FALLBACK_CONFIG);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [storage, setStorage] = useState<StorageState | null>(null);
  const [vault, setVault] = useState<VaultState>(getVaultState);
  const [ready, setReady] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [secondFactor, setSecondFactor] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [completedShares, setCompletedShares] = useState(0);
  const [restoreTried, setRestoreTried] = useState(false);
  const [theme, setThemeState] = useState<'light' | 'dark'>(initialTheme);

  // The design system reads `data-theme` off the document, so the toggle is one
  // attribute rather than a second set of styles.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Not worth surfacing — the theme still applies for this session.
    }
  }, [theme]);

  useEffect(() => subscribe(setVault), []);

  /**
   * Presence: one socket for the page, and only while somebody is signed in.
   *
   * Keyed on the id rather than on `me` — `refresh` hands back a fresh object
   * on every poll, and reconnecting a socket each time would be a reconnect a
   * minute.
   *
   * `path` is this app's own proxy, not mauvely.com: the socket is same-origin
   * so `connect-src 'self'` covers it, which is the whole reason
   * `worker/presence.ts` exists. It is deliberately **not** keyed on the vault
   * — presence says somebody is at their desk, which is true whether or not
   * they have typed their password yet.
   *
   * `stopPresence` on the way out is not tidiness. The socket is what says
   * somebody is here, and one that outlived the session would keep saying it
   * about whoever signs in next at this browser. A stranger reading a public
   * link on `/s/…` renders inside this provider and has no `me`, so nothing
   * here opens a socket for them.
   */
  useEffect(() => {
    if (!me?.user.id) {
      stopPresence();
      return;
    }
    startPresence({ app: 'cloud', path: '/api/cloud/presence' });
    return () => stopPresence();
  }, [me?.user.id]);

  // Keys cached in nodes.ts are derived from the vault, so they must not
  // outlive it. Clearing here rather than inside `lock()` keeps the crypto
  // module free of any dependency on the app's data layer.
  useEffect(() => {
    if (vault.status === 'locked') {
      forgetKeys();
      // Workspace root keys are held in their own map — keyed on workspace id,
      // because the seam has to answer "do I have this workspace" before it
      // knows the root's node id. Both must go together or a re-unlock would
      // find a stale envelope cached.
      forgetWorkspaceKeys();
    }
  }, [vault.status]);

  /**
   * Restore a trusted device without asking for the password.
   *
   * "Trust this device" persisted the master key to IndexedDB and nothing ever
   * read it back, so the promise on the checkbox — "stay unlocked here" — was
   * never kept and every reload asked for the password again. `restoreTrusted`
   * was written for this and had no callers.
   *
   * Runs once the session is known and the vault is locked. A stored key that
   * no longer opens the vault is dropped inside `restoreTrusted`, so a failure
   * here simply leaves the unlock screen up.
   */
  useEffect(() => {
    if (!me?.hasVault) return;
    if (vault.status === 'unlocked' || restoreTried) return;
    // A picker opened by any page but Relay unlocks nothing: it shares that
    // page's browsing-context group (`picker.ts`).
    if (isPickerPath(window.location.pathname) && !pickerGateHere().ok) return;
    let cancelled = false;
    void (async () => {
      try {
        // Check IndexedDB before asking the server for anything. `GET /vault`
        // shares the unlock rate limit — 30 per 15 minutes — so spending one on
        // every page load, for the majority who never ticked the box, would
        // eventually lock someone out of their own vault. This way the request
        // only happens when there is a key to use with it.
        // An explicit lock on a trusted device answers false here too, so a
        // refresh does not undo it.
        if (!(await isRestorable())) return;
        const blobs = await api.vault.get();
        if (!cancelled) await restoreTrusted(blobs);
      } catch {
        // No stored key, storage blocked, or the blobs did not load. The
        // password path is right there.
      } finally {
        // Attempted once, whatever the outcome. Held apart from the result so
        // the unlock screen does not flash up before a restore lands.
        if (!cancelled) setRestoreTried(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me?.hasVault, vault.status, restoreTried]);

  /**
   * Finish any invitations whose recipient has since created a vault.
   *
   * Unlock is the only moment this can happen: the wrap needs the node key,
   * which needs the master key, which exists only while the vault is open. It
   * runs quietly in the background — the owner has already done their part, and
   * the recipient is the one waiting.
   */
  useEffect(() => {
    if (vault.status !== 'unlocked') return;
    let cancelled = false;
    // All three kinds of invitation are stuck on the same thing — a key only a
    // member's browser can wrap — so all three are finished at the same moment.
    void Promise.all([
      completePendingShares(),
      completePendingWorkspaceKeys(),
      completePendingProjectKeys()
    ]).then(
      ([shares, workspaces]) => {
        const n = shares + workspaces;
        if (!cancelled && n > 0) setCompletedShares((prev) => prev + n);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [vault.status]);

  const refresh = useCallback(async () => {
    try {
      const next = await api.me();
      setMe(next);
      // Applied here rather than in an effect on `me`: this is the one place
      // the server's answer arrives, and an effect would repaint a frame later.
      applyBranding(next.branding);
      setStorage(next.storage);
      setSignedOut(false);
      setSecondFactor(false);
      setLoadError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSignedOut(true);
        setMe(null);
        // A signed-out viewer is nobody's member. Keeping the colour would
        // leave a customer's branding on a sign-in screen that belongs to
        // Mauvely, and on a shared machine it would leak which company the
        // last person worked for.
        applyBranding(null);
      } else if (err instanceof ApiError && err.code === 'second_factor_required') {
        setSecondFactor(true);
        setMe(null);
        applyBranding(null);
      } else {
        throw err;
      }
    }
  }, []);

  const refreshStorage = useCallback(async () => {
    try {
      setStorage(await api.storage());
    } catch {
      // The quota line goes stale rather than the page failing.
    }
  }, []);

  /**
   * Load the session once on mount.
   *
   * Every branch here is deliberate. The original recorded only a 401 and then
   * let the status expression treat a null `me` as signed out — so a 500, a
   * cold start, or a dropped connection all bounced the user to mauvely.com to
   * sign in, when they were signed in the whole time. Landing on /signin with a
   * live session and signing in again reproduced the same transient, which is
   * what made it read as a loop rather than as an outage.
   *
   * A failure we cannot attribute to the session is now its own state with a
   * retry. Being unable to reach the server is not the same as being unwelcome.
   */
  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([api.config(), api.me()]).then(([configResult, meResult]) => {
      if (cancelled) return;
      if (configResult.status === 'fulfilled') setConfig(configResult.value);

      if (meResult.status === 'fulfilled') {
        setMe(meResult.value);
        // The same call `refresh` makes, and it has to be here too: this is the
        // *first* load, and it was the one path that set `me` without it. The
        // cached accent hid that — a returning viewer was painted from
        // `localStorage` before this resolved — so the gap only showed on a
        // first-ever visit, which is the one visit a customer's own staff make.
        applyBranding(meResult.value.branding);
        setStorage(meResult.value.storage);
        setSignedOut(false);
        setSecondFactor(false);
        setLoadError(null);
      } else {
        const err = meResult.reason instanceof ApiError ? meResult.reason : null;
        if (err?.status === 401) {
          setSignedOut(true);
          applyBranding(null);
        } else if (err?.code === 'second_factor_required') {
          setSecondFactor(true);
          applyBranding(null);
        } else {
          // Deliberately *not* cleared here. The two branches above are answers
          // — nobody is established as a member — and clearing also empties the
          // cache, which is what stops the next load flashing Mauvely. This
          // branch is "could not ask", and dropping a customer's brand every
          // time the network hiccups would repaint their product as ours for
          // the duration of an outage.
          setLoadError(err?.message ?? 'Could not reach your cloud.');
        }
      }
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setReady(false);
    setLoadError(null);
    setAttempt((n) => n + 1);
  }, []);

  // A vault that might restore itself from a trusted device is still 'loading':
  // showing the unlock screen and then replacing it a moment later reads as a
  // glitch, and the attempt is one IndexedDB read plus one request.
  const restoring = !!me?.hasVault && vault.status !== 'unlocked' && !restoreTried;

  const status: AppStatus = !ready || restoring
    ? 'loading'
    : signedOut
      ? 'signedOut'
      : secondFactor
        ? 'secondFactor'
        : // Only an attributed failure sends anyone away. Anything else — and
          // anything that left `me` unset without saying why — stops here.
          !me
          ? 'unreachable'
          : !me.hasVault
            ? 'noVault'
            : vault.status === 'unlocked'
              ? 'unlocked'
              : 'locked';

  const value = useMemo<AppState>(
    () => ({
      status,
      config,
      me,
      storage,
      vault,
      theme,
      setTheme: setThemeState,
      refresh,
      refreshStorage,
      lockVault: lock,
      loadError,
      retry,
      completedShares
    }),
    [
      status,
      config,
      me,
      storage,
      vault,
      theme,
      refresh,
      refreshStorage,
      loadError,
      retry,
      completedShares
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp used outside AppProvider');
  return ctx;
}

/**
 * The organisation's branding, or null for Mauvely.
 *
 * Deliberately **not** on `AppContext`. The public share page renders with no
 * provider at all — no session, no `me`, no shell — and it draws the lockup
 * like every other page. A context hook would throw there, and threading
 * `branding` down as a prop would put it on all three call sites in this repo
 * and all ten in Review.
 *
 * So it reads the same module-level store `applyBranding` writes, which is the
 * accent's model exactly: a global the chrome reads, set in one place.
 */
export function useBranding(): Branding | null {
  return useSyncExternalStore(subscribeBranding, getBranding, getBranding);
}
